import { startTransition } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { selectAccount } from '@packages/api/endpoints/auth.js';
import { useAuth } from '../lib/auth/useAuth';

export interface SwitchAccountInput {
  /** The account the JWT should be re-scoped to. */
  accountId: string;
  /** Where to land once the new token is in auth state. */
  to: string;
  /**
   * Replace the current history entry instead of pushing one. Used by the
   * practice picker, which the user should not be able to go back to.
   */
  replace?: boolean;
}

export interface UseSwitchAccountOptions {
  /**
   * Handle the failure locally. Supplying this also suppresses the global
   * error toast, which the App-level MutationCache skips whenever a mutation
   * carries its own `onError`.
   */
  onError?: (error: Error) => void;
}

/**
 * Switches the signed-in user's active account: exchanges the current JWT for
 * one scoped to `accountId`, puts it in auth state, then navigates to `to`.
 *
 * Every switch goes through here - picking a practice at sign-in, opening an
 * administered entity's books, and coming back out of them - so the token
 * exchange and the `login()` that has to follow it cannot drift apart between
 * those surfaces. A switch that navigated before `login()` would land on a page
 * still holding the previous account's token.
 *
 * `login()` and `navigate()` run in one transition so the new account and the
 * new location commit in the same render. Applied separately, `login()` could
 * commit first and re-render the old page under the new account, which then
 * fetches its records (a client by id, say) from an account they do not belong
 * to.
 */
export function useSwitchAccount(options?: UseSwitchAccountOptions) {
  const { login, user } = useAuth();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async ({ accountId }: SwitchAccountInput) => {
      // Every caller sits behind a route guard, so a missing user is an
      // invariant violation rather than a state to render around.
      if (!user) throw new Error('Cannot switch account before the signed-in user is known');
      const { token } = await selectAccount(accountId);
      return { token, user };
    },
    onSuccess: ({ token, user: profile }, { to, replace }) => {
      startTransition(() => {
        login(token, profile);
        if (replace) navigate(to, { replace: true });
        else navigate(to);
      });
    },
    ...(options?.onError ? { onError: options.onError } : {}),
  });
}
